import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ObsTileFields } from "@/features/obs/components/ObsTileFields";
import { useObsWizard } from "@/features/obs/hooks/obs-wizard-context";

/** Step 1 — what the report is called and where it will live. */
export function StepIdentity() {
  const { draft, patch, pickTile, slugValid, slugConflict, slugCheck, busy } =
    useObsWizard();
  const previewPath = `/obs-reports/${draft.slug}`;
  // An empty title would leave the sentence naming nothing at all; the slug is
  // the report's other name and always present.
  const takenBy = slugConflict
    ? slugConflict.title.ar || slugConflict.slug
    : "";

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="obs-title-ar">اسم التقرير (عربي)</Label>
        <Input
          id="obs-title-ar"
          value={draft.titleAr}
          onChange={(e) => patch({ titleAr: e.target.value })}
          placeholder="مثال: تنفيذ الموازنة لعام ٢٠٢٥"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="obs-title-en">اسم التقرير (إنجليزي)</Label>
        <Input
          id="obs-title-en"
          dir="ltr"
          value={draft.titleEn}
          onChange={(e) => patch({ titleEn: e.target.value })}
          placeholder="Budget Execution 2025"
        />
        <p className="text-xs text-muted-foreground">
          يُستخدم في النسخة الإنجليزية من الموقع. إن تُرك فارغًا سيظهر الاسم
          العربي.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="obs-slug">الرابط</Label>
        <Input
          id="obs-slug"
          dir="ltr"
          className="font-mono"
          value={draft.slug}
          // The only field the server will not patch: it is the report's address,
          // the key its datasets are filed under, and the confirm token its
          // deletion requires. Editable until the report exists, locked the moment
          // it does — rather than accepting a change that silently would not save,
          // or would create a second report.
          disabled={Boolean(draft.createdSlug)}
          onChange={(e) => patch({ slug: e.target.value.trim().toLowerCase() })}
          placeholder="budget-execution-2025"
        />
        <p className="text-xs text-muted-foreground">
          حروف إنجليزية صغيرة وأرقام وشرطات فقط. سيكون عنوان الصفحة{" "}
          <span dir="ltr" className="font-mono">
            {previewPath}
          </span>{" "}
          ولا يمكن تغييره بعد الإنشاء.
        </p>
        {draft.createdSlug && (
          <p className="text-xs text-amber-600">
            أُنشئ التقرير بهذا الرابط، فلم يعد قابلًا للتغيير. بقية الحقول ما
            زالت قابلة للتعديل ويُحفظ ما تغيّره عند المتابعة.
          </p>
        )}
        {draft.slug && !slugValid && (
          <p className="text-xs text-red-600">
            الرابط غير صالح — استخدم حروفًا إنجليزية صغيرة وأرقامًا وشرطات مفردة
            فقط.
          </p>
        )}

        {/*
          🔴 SAID HERE, ON STEP ONE. This used to surface only when the report
          was created on the units step — after a workbook, a column mapping and
          the units had been entered, none of which can be carried to a different
          slug.
        */}
        {slugConflict && (
          <p className="text-xs text-red-600">
            {slugConflict.isDeclared
              ? `هذا الرابط يخص أحد التقارير الستة الأصلية «${takenBy}» — اختر رابطًا آخر.`
              : `الرابط مستخدم بالفعل في تقرير «${takenBy}» — اختر رابطًا آخر.`}
          </p>
        )}

        {/*
          And say which of the other three answers it is. The operator is about
          to spend four steps on this address, and silence does not distinguish
          "available" from "not checked" — nor may a check that FAILED be
          reported as an available slug.
        */}
        {slugCheck === "checking" && (
          <p className="text-xs text-muted-foreground">
            جارٍ التحقق من توفر الرابط…
          </p>
        )}
        {slugCheck === "free" && (
          <p className="text-xs text-emerald-600">الرابط متاح.</p>
        )}
        {slugCheck === "unknown" && (
          <p className="text-xs text-amber-600">
            تعذّر التحقق من توفر الرابط الآن. يمكنك المتابعة، لكن سيُرفض الإنشاء
            في خطوة «الوحدات» إن كان الرابط مستخدمًا.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>صور البطاقة (اختياري)</Label>
        <ObsTileFields
          sizes={draft.tileSizes}
          picked={draft.tileNames}
          disabled={busy}
          onPick={pickTile}
        />
      </div>
    </div>
  );
}
